// 专家经验蒸馏页 (Experience Distillation) — v4 大赛版
// - 蒸馏为主功能（居中大表单），经验库为右侧滑出抽屉
// - 游客模式：mock 经验库 + 火山引擎真实蒸馏
// - 教师视角：完整 CRUD；游客视角：只读演示 + 可蒸馏

import { useEffect, useMemo, useState } from 'react';
import { experienceApi, type ExperienceRule, type DistilledExperience, type FromRecordPreview, type HighScoreRecord } from '@/services/api';
import { useAuthStore } from '@/stores/authStore';
import { usePageStore } from '@/stores/pageStore';
import { useUIStore } from '@/stores/uiStore';
import { useToastStore } from '@/components/Toast';
import { SCENES } from '@/templates';
import TimelineCompare from '@/components/TimelineCompare';
import { chatOnce } from '@/services/volcano';
import {
  ArrowLeft, Plus, RefreshCw, Loader2, BookOpen, Lightbulb, AlertTriangle,
  Target, Clock, Zap, User, ChevronDown, ChevronUp, Trash2, Edit3, Save, X, Sparkles,
  Wand2, PlayCircle, Database, PanelRightOpen, PanelRightClose,
} from 'lucide-react';
import { confirmDialog } from '@/components/ConfirmDialog';
import type { ExpertTimeline } from '@/services/timeline';

// =============================================================
// 常量
// =============================================================

const SOURCE_OPTIONS: { value: string; label: string }[] = [
  { value: 'think_aloud', label: '口述实录' },
  { value: 'interview', label: '事后访谈' },
  { value: 'observation', label: '操作观察' },
  { value: 'from_record', label: '从演练记录' },
];

const DISTILL_TIPS = [
  '正在分析专家口述内容…',
  '提取关键操作决策…',
  '识别参数敏感区间…',
  '总结经验直觉与常见错误…',
  '凝练核心经验洞察…',
];

const PRIORITY_STYLE: Record<string, string> = {
  critical: 'bg-red-500/15 text-red-400 border-red-500/40',
  recommended: 'bg-blue-500/15 text-blue-400 border-blue-500/40',
};
const PRIORITY_LABEL: Record<string, string> = {
  critical: '关键',
  recommended: '建议',
};

const fmtTime = (ts: number) =>
  new Date(ts).toLocaleString('zh-CN', { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' });

const sceneOptions = Object.entries(SCENES).map(([id, pack]) => ({ id, name: pack.meta.name }));
const sceneName = (id: string) => SCENES[id]?.meta.name ?? id;

const inputCls =
  'w-full px-3 py-2.5 text-sm bg-bg-tertiary border border-border rounded-lg text-text-primary placeholder:text-text-muted focus:outline-none focus:border-primary/60 transition-colors';
const labelCls = 'block text-xs font-medium text-text-secondary mb-1.5';

// 场景配色
const SCENE_STYLES: Record<string, { bar: string; badge: string; dot: string }> = {
  fcc:         { bar: 'border-l-orange-500', badge: 'bg-orange-500/15 text-orange-400', dot: 'bg-orange-500' },
  portcrane:   { bar: 'border-l-blue-500',   badge: 'bg-blue-500/15 text-blue-400',   dot: 'bg-blue-500' },
  windturbine: { bar: 'border-l-emerald-500', badge: 'bg-emerald-500/15 text-emerald-400', dot: 'bg-emerald-500' },
};
const getSceneStyle = (id: string) => SCENE_STYLES[id] ?? { bar: 'border-l-gray-500', badge: 'bg-gray-500/15 text-gray-400', dot: 'bg-gray-500' };

// =============================================================
// 游客模式 mock 数据
// =============================================================
const GUEST_EXPERIENCES: ExperienceRule[] = [
  {
    id: 'ge1', scene_id: 'fcc', fault_id: null, fault_name: '再生器温度异常升高',
    title: '再生器超温的先兆判断与处置节奏',
    expert_name: '李建国', expert_title: '高级技师·28年',
    source_type: 'think_aloud', status: 'active',
    raw_transcript: '再生器温度往上跑，第一反应不是去降温，是先看进料量。进料量要是没变，那肯定是燃烧室出了问题。这时候不能急着降温度，得先把反应温度稳住，然后再慢慢调。新手最大的错误就是一看温度高就猛降温，结果催化剂结焦了。',
    created_at: Date.now() - 86400000 * 7, updated_at: Date.now() - 86400000 * 7,
    distilled: {
      master_insight: '再生器超温处置的核心在于"先稳后降"——先确认进料量是否变化以判断根因，再以小幅度逐步降温，避免催化剂结焦。',
      key_decisions: [
        { step: 1, action: '检查进料量是否变化', priority: 'critical', timing: '0-30秒', reasoning: '进料量变化会直接影响燃烧室热平衡，是判断根因的第一依据' },
        { step: 2, action: '稳定反应温度', priority: 'critical', timing: '30-60秒', reasoning: '先稳住温度防止连锁反应，为后续处置争取时间' },
        { step: 3, action: '小幅度逐步降温', priority: 'recommended', timing: '60-180秒', reasoning: '每次调节幅度不超过5°C，避免温度骤降导致催化剂结焦' },
      ],
      param_thresholds: [
        { param: '再生器温度', danger_zone: '> 720°C', expert_action: '立即检查进料，小幅降温', normal_range: '680-710°C' },
        { param: '进料量', danger_zone: '波动>5%', expert_action: '稳定进料，排查管路', normal_range: '±2%' },
      ],
      rhythm: '前30秒判断根因，30-60秒稳定系统，60-180秒逐步降温，全程不超过3分钟完成关键处置',
      intuition_rules: [
        '温度升+进料不变 = 燃烧室问题',
        '降温速度永远不超过5°C/次',
        '先稳后降，急降必结焦',
      ],
      common_mistakes: [
        { mistake: '一看超温就猛降温', why_wrong: '温度骤降导致催化剂结焦，造成更大损失', correct_alternative: '每次降温不超过5°C，间隔30秒观察' },
        { mistake: '忽略进料量检查', why_wrong: '未找到根因，降温只是治标', correct_alternative: '第一时间检查进料量变化' },
      ],
    },
  },
  {
    id: 'ge2', scene_id: 'portcrane', fault_id: null, fault_name: '大车大风报警',
    title: '港机大风工况的制动与锚定流程',
    expert_name: '张德海', expert_title: '高级技师·22年',
    source_type: 'interview', status: 'active',
    raw_transcript: '大风报警一响，先制动再收臂，最后锚定。顺序不能反。好多人先收臂，结果臂被风吹跑了。制动之后等风速降下来再操作，别急。',
    created_at: Date.now() - 86400000 * 3, updated_at: Date.now() - 86400000 * 3,
    distilled: {
      master_insight: '大风工况处置的铁律是"制动→收臂→锚定"三步顺序，不可颠倒。制动后必须等风速降至安全值再操作。',
      key_decisions: [
        { step: 1, action: '大车制动', priority: 'critical', timing: '0-10秒', reasoning: '先锁死大车防止溜车，这是最关键的安全动作' },
        { step: 2, action: '收臂至安全位置', priority: 'critical', timing: '10-60秒', reasoning: '制动后再收臂，避免臂受风力失控' },
        { step: 3, action: '部署锚定装置', priority: 'recommended', timing: '60-120秒', reasoning: '锚定是最后的安全保障，防止制动失效' },
      ],
      param_thresholds: [
        { param: '风速', danger_zone: '> 20m/s', expert_action: '立即制动+收臂+锚定', normal_range: '< 15m/s' },
      ],
      rhythm: '10秒制动，50秒收臂，60秒锚定，全程2分钟内完成',
      intuition_rules: [
        '大风先制动，别管别的',
        '风速没降下来别操作',
        '锚定是最后一道保险，不能省',
      ],
      common_mistakes: [
        { mistake: '先收臂再制动', why_wrong: '臂在无制动状态下受风力作用，可能失控', correct_alternative: '严格按制动→收臂→锚定顺序' },
        { mistake: '制动后立即操作', why_wrong: '风速未降至安全值，操作有风险', correct_alternative: '等风速降至15m/s以下再操作' },
      ],
    },
  },
  {
    id: 'ge3', scene_id: 'windturbine', fault_id: null, fault_name: '齿轮箱高温',
    title: '风机齿轮箱高温的现场判断与应急处理',
    expert_name: '王秀兰', expert_title: '技师·15年',
    source_type: 'observation', status: 'active',
    raw_transcript: '齿轮箱温度高，先看油位。油位低就补油，油位正常就查冷却系统。冷却系统没问题，那就是齿轮磨损了，得停机检查。别一上来就停机，能不停就不停。',
    created_at: Date.now() - 86400000, updated_at: Date.now() - 86400000,
    distilled: {
      master_insight: '齿轮箱高温处置遵循"油位→冷却→齿轮"三级排查法，能不停机就不停机，按层级逐步排查根因。',
      key_decisions: [
        { step: 1, action: '检查润滑油油位', priority: 'critical', timing: '0-60秒', reasoning: '油位低是最常见也最易处理的原因' },
        { step: 2, action: '检查冷却系统运行状态', priority: 'critical', timing: '60-120秒', reasoning: '冷却系统故障是第二常见原因' },
        { step: 3, action: '停机检查齿轮磨损', priority: 'recommended', timing: '120秒+', reasoning: '前两级排查无果时，需停机开盖检查' },
      ],
      param_thresholds: [
        { param: '齿轮箱温度', danger_zone: '> 85°C', expert_action: '立即排查，必要时停机', normal_range: '60-75°C' },
        { param: '油位', danger_zone: '< MIN线', expert_action: '补油至正常范围', normal_range: 'MIN-MAX之间' },
      ],
      rhythm: '1分钟查油位，1分钟查冷却，最后才停机检查',
      intuition_rules: [
        '油位低就补油，简单直接',
        '冷却系统看风扇和泵',
        '能不停机就不停机',
      ],
      common_mistakes: [
        { mistake: '高温直接停机', why_wrong: '未排查根因，可能只是油位低，停机影响发电效率', correct_alternative: '先排查油位和冷却系统' },
      ],
    },
  },
];

// =============================================================
// 主页面
// =============================================================
export const ExperienceCollectPage: React.FC = () => {
  const user = useAuthStore((s) => s.user);
  const setPage = usePageStore((s) => s.setPage);
  const activeTemplate = useUIStore((s) => s.activeTemplate);
  const pushToast = useToastStore((s) => s.push);
  const isGuest = user?.id === 'guest';

  const [form, setForm] = useState({
    scene_id: activeTemplate ?? sceneOptions[0]?.id ?? '',
    fault_name: '',
    title: '',
    expert_name: '',
    expert_title: '',
    source_type: 'think_aloud',
    raw_transcript: '',
  });

  const [distilling, setDistilling] = useState(false);
  const [tipIdx, setTipIdx] = useState(0);
  const [latestDistilled, setLatestDistilled] = useState<DistilledExperience | null>(null);
  const [experiences, setExperiences] = useState<ExperienceRule[]>([]);
  const [listLoading, setListLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [redistillingId, setRedistillingId] = useState<string | null>(null);
  // 右侧抽屉
  const [drawerOpen, setDrawerOpen] = useState(false);

  // 从演练记录提升
  const [myRecords, setMyRecords] = useState<HighScoreRecord[]>([]);
  const [showFromRecord, setShowFromRecord] = useState(false);
  const [selectedRecordId, setSelectedRecordId] = useState<string>('');
  const [preview, setPreview] = useState<FromRecordPreview | null>(null);
  const [draftTimeline, setDraftTimeline] = useState<ExpertTimeline | null>(null);
  const [previewPlayerOpen, setPreviewPlayerOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);

  useEffect(() => {
    if (!distilling) { setTipIdx(0); return; }
    const t = setInterval(() => setTipIdx((i) => (i + 1) % DISTILL_TIPS.length), 2600);
    return () => clearInterval(t);
  }, [distilling]);

  const loadList = async () => {
    setListLoading(true);
    try {
      if (isGuest) { setExperiences(GUEST_EXPERIENCES); return; }
      const r = await experienceApi.list();
      setExperiences(r.experiences);
    } catch (e: any) {
      pushToast({ type: 'warning', title: '加载经验库失败', description: e?.message ?? '请稍后重试' });
    } finally {
      setListLoading(false);
    }
  };

  useEffect(() => {
    void loadList();
    if (!isGuest) {
      experienceApi.highScorePool({ limit: 100 })
        .then((r) => setMyRecords(r.records))
        .catch(() => setMyRecords([]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredCandidates = useMemo(() => {
    if (!form.scene_id) return myRecords;
    return myRecords.filter((r) => r.scene_id === form.scene_id);
  }, [myRecords, form.scene_id]);

  const handleLoadPreview = async (recordId: string) => {
    if (!recordId || isGuest) return;
    setPreviewLoading(true); setPreview(null); setDraftTimeline(null);
    try {
      const r = await experienceApi.fromRecordPreview(recordId);
      setPreview(r); setDraftTimeline(r.timeline);
      setForm((f) => ({ ...f, scene_id: r.record.scene_id, fault_name: r.record.fault_name, title: r.suggested_title, raw_transcript: r.suggested_transcript, source_type: 'from_record' }));
      pushToast({ type: 'success', title: '已生成时间轴草稿', description: `共 ${r.timeline.events.length} 个事件 / 时长 ${r.timeline.duration_sec}s` });
    } catch (e: any) {
      pushToast({ type: 'warning', title: '生成预览失败', description: e?.message ?? '请稍后重试' });
    } finally { setPreviewLoading(false); }
  };

  const handleRemoveDraftEvent = (idx: number) => {
    if (!draftTimeline) return;
    setDraftTimeline({ ...draftTimeline, events: draftTimeline.events.filter((_, i) => i !== idx) });
  };

  const handleClearPreview = () => { setPreview(null); setDraftTimeline(null); setSelectedRecordId(''); };

  /** 游客模式蒸馏：基于口述内容调用火山引擎 */
  const guestDistill = async (transcript: string, faultName: string, sceneLabel: string): Promise<DistilledExperience> => {
    const prompt = `你是工业实训经验蒸馏专家。请将以下老专家的口述记录蒸馏为结构化教学经验。

场景：${sceneLabel}
故障：${faultName}

专家口述记录：
${transcript}

请严格按照以下JSON格式输出（不要输出其他内容，不要用markdown代码块包裹）：
{
  "master_insight": "一句话总结核心经验",
  "key_decisions": [
    {"step": 1, "action": "操作名称", "priority": "critical或recommended", "timing": "时间窗口", "reasoning": "为什么这样做"}
  ],
  "param_thresholds": [
    {"param": "参数名", "danger_zone": "危险区间", "expert_action": "专家处置", "normal_range": "正常范围"}
  ],
  "rhythm": "操作节奏描述",
  "intuition_rules": ["经验直觉1", "经验直觉2"],
  "common_mistakes": [
    {"mistake": "错误做法", "why_wrong": "为什么错", "correct_alternative": "正确做法"}
  ]
}`;
    const raw = await chatOnce(
      [
        { role: 'system', content: '你是一位资深的工业实训教学专家，擅长从老专家的口述记录中提炼结构化的教学经验。输出必须是纯JSON格式，不要包含markdown代码块标记。' },
        { role: 'user', content: prompt },
      ],
      { temperature: 0.4, maxTokens: 4096 },
    );
    const jsonStr = raw.replace(/```json\s*/g, '').replace(/```\s*/g, '').trim();
    return JSON.parse(jsonStr);
  };

  const handleCollect = async () => {
    if (!form.scene_id || !form.fault_name.trim() || !form.title.trim() || !form.raw_transcript.trim()) {
      pushToast({ type: 'warning', title: '请填写完整信息', description: '场景、故障名称、经验标题与专家口述记录为必填项' });
      return;
    }
    setDistilling(true);
    setLatestDistilled(null);
    try {
      if (isGuest) {
        const distilled = await guestDistill(form.raw_transcript.trim(), form.fault_name.trim(), sceneName(form.scene_id));
        const newExp: ExperienceRule = {
          id: `guest-${Date.now()}`, scene_id: form.scene_id, fault_id: null, fault_name: form.fault_name.trim(),
          title: form.title.trim(), expert_name: form.expert_name.trim() || null,
          expert_title: form.expert_title.trim() || null, source_type: form.source_type,
          status: 'active', raw_transcript: form.raw_transcript.trim(),
          created_at: Date.now(), updated_at: Date.now(), distilled,
        };
        setExperiences((prev) => [newExp, ...prev]);
        setLatestDistilled(distilled);
        pushToast({ type: 'success', title: '蒸馏完成', description: 'AI已将专家口述提炼为结构化教学知识' });
      } else {
        const resp = await experienceApi.collect({
          scene_id: form.scene_id, fault_name: form.fault_name.trim(),
          fault_id: preview?.record.fault_id, title: form.title.trim(),
          raw_transcript: form.raw_transcript.trim(),
          expert_name: form.expert_name.trim() || undefined,
          expert_title: form.expert_title.trim() || undefined,
          source_type: form.source_type, timeline: draftTimeline,
          source_record_id: preview?.record.id,
        });
        if (resp.distill_error) { pushToast({ type: 'warning', title: '蒸馏未成功', description: resp.distill_error }); }
        else if (resp.distilled) { setLatestDistilled(resp.distilled); pushToast({ type: 'success', title: '蒸馏完成', description: '专家经验已提炼为结构化教学知识' }); }
        else { pushToast({ type: 'info', title: '已保存', description: resp.message ?? '经验记录已入库' }); }
        await loadList();
      }
      setForm((f) => ({ ...f, fault_name: '', title: '', expert_name: '', expert_title: '', raw_transcript: '' }));
      handleClearPreview();
    } catch (e: any) {
      pushToast({ type: 'warning', title: '采集失败', description: e?.message ?? '请稍后重试' });
    } finally { setDistilling(false); }
  };

  const handleRedistill = async (id: string) => {
    if (isGuest) {
      const exp = experiences.find((e) => e.id === id);
      if (!exp?.raw_transcript) return;
      setRedistillingId(id);
      try {
        const distilled = await guestDistill(exp.raw_transcript, exp.fault_name, sceneName(exp.scene_id));
        setExperiences((prev) => prev.map((e) => e.id === id ? { ...e, distilled } : e));
        pushToast({ type: 'success', title: '重新蒸馏完成' });
      } catch (e: any) {
        pushToast({ type: 'warning', title: '重新蒸馏失败', description: e?.message ?? '请稍后重试' });
      } finally { setRedistillingId(null); }
      return;
    }
    setRedistillingId(id);
    try {
      const r = await experienceApi.redistill(id);
      pushToast({ type: 'success', title: '重新蒸馏完成', description: r.message ?? '已更新结构化经验' });
      await loadList();
    } catch (e: any) {
      pushToast({ type: 'warning', title: '重新蒸馏失败', description: e?.message ?? '请稍后重试' });
    } finally { setRedistillingId(null); }
  };

  const handleArchive = async (exp: ExperienceRule) => {
    if (isGuest) return;
    const ok = await confirmDialog({ title: `确认归档"${exp.title}"？`, description: '归档后该经验将不再出现在默认列表中。', icon: 'question', confirmText: '归档' });
    if (!ok) return;
    try { await experienceApi.update(exp.id, { status: 'archived' }); pushToast({ type: 'success', title: '已归档' }); await loadList(); }
    catch (e: any) { pushToast({ type: 'warning', title: '归档失败', description: e?.message ?? '请稍后重试' }); }
  };

  const handleDelete = async (exp: ExperienceRule) => {
    if (isGuest) return;
    const ok = await confirmDialog({ title: `确认删除"${exp.title}"？`, description: '此操作不可撤销。', danger: true, confirmText: '删除' });
    if (!ok) return;
    try { await experienceApi.remove(exp.id); pushToast({ type: 'success', title: '已删除' }); if (expandedId === exp.id) setExpandedId(null); await loadList(); }
    catch (e: any) { pushToast({ type: 'warning', title: '删除失败', description: e?.message ?? '请稍后重试' }); }
  };

  const handleRename = async (exp: ExperienceRule, title: string) => {
    if (isGuest) return;
    try { await experienceApi.update(exp.id, { title }); pushToast({ type: 'success', title: '已更新标题' }); await loadList(); }
    catch (e: any) { pushToast({ type: 'warning', title: '更新失败', description: e?.message ?? '请稍后重试' }); throw e; }
  };

  const canSubmit = !distilling && !!form.scene_id && form.fault_name.trim().length > 0 && form.title.trim().length > 0 && form.raw_transcript.trim().length > 0;

  const sceneCount = new Set(experiences.map((e) => e.scene_id)).size;
  const expertCount = new Set(experiences.filter((e) => e.expert_name).map((e) => e.expert_name)).size;

  return (
    <div className="h-screen overflow-hidden bg-bg-primary text-text-primary flex flex-col">
      {/* —— 顶部导航 —— */}
      <div className="flex-shrink-0 border-b border-border bg-bg-secondary/80 backdrop-blur-sm">
        <div className="max-w-5xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => setPage('dashboard')} className="p-2 -ml-2 text-text-muted hover:text-text-primary rounded-lg hover:bg-bg-tertiary transition-colors" title="返回教学看板">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-lg font-bold flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-cyan-400" />
                专家经验蒸馏
                {isGuest && <span className="text-xs px-2 py-0.5 bg-cyan-500/15 text-cyan-400 rounded-full">游客演示</span>}
              </h1>
              <p className="text-xs text-text-muted mt-0.5">
                {isGuest ? 'AI真实蒸馏 · 输入专家口述即可提炼结构化教学知识' : '采集老专家经验，AI自动提炼结构化教学知识'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* 统计迷你标签 */}
            <div className="hidden sm:flex items-center gap-3 text-xs text-text-muted mr-2">
              <span className="flex items-center gap-1"><Database className="w-3.5 h-3.5 text-primary" />{experiences.length}</span>
              <span className="flex items-center gap-1"><BookOpen className="w-3.5 h-3.5 text-cyan-400" />{sceneCount}场景</span>
              <span className="flex items-center gap-1"><User className="w-3.5 h-3.5 text-amber-400" />{expertCount}专家</span>
            </div>
            <button
              onClick={() => setDrawerOpen(true)}
              className="px-3 py-1.5 text-xs bg-bg-tertiary hover:bg-bg-secondary border border-border rounded-lg flex items-center gap-1.5 transition-colors text-text-secondary hover:text-text-primary"
            >
              <PanelRightOpen className="w-3.5 h-3.5" />
              经验库 ({experiences.length})
            </button>
          </div>
        </div>
      </div>

      {/* —— 主体：蒸馏工作台（居中） —— */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-3xl mx-auto px-6 py-8">
          {/* 采集表单 */}
          <div className="rounded-xl border border-border bg-bg-secondary overflow-hidden shadow-lg">
            <div className="px-6 py-4 border-b border-border bg-gradient-to-r from-cyan-500/10 to-transparent">
              <h2 className="text-base font-bold flex items-center gap-2">
                <Plus className="w-4 h-4 text-cyan-400" /> 新建经验采集
              </h2>
              <p className="text-xs text-text-muted mt-0.5">填写专家口述记录，AI自动蒸馏为结构化教学知识</p>
            </div>
            <div className="p-6 space-y-4">
              {/* 从演练记录提升（仅正式用户） */}
              {!isGuest && (
                <div className="rounded-lg border border-cyan-500/30 bg-cyan-500/5 p-3">
                  <button type="button" onClick={() => setShowFromRecord((v) => !v)} className="flex w-full items-center justify-between text-left">
                    <span className="flex items-center gap-2 text-sm font-medium text-cyan-300">
                      <Wand2 className="h-4 w-4" /> 从高分演练记录一键提升为时间轴
                    </span>
                    {showFromRecord ? <ChevronUp className="h-4 w-4 text-cyan-300" /> : <ChevronDown className="h-4 w-4 text-cyan-300" />}
                  </button>
                  {showFromRecord && (
                    <div className="mt-3 space-y-3">
                      <div className="text-xs text-text-secondary">从全班学员的 S/A 评级演练中挑一个，系统自动把操作序列转换为时间轴草稿。</div>
                      <select value={selectedRecordId} onChange={(e) => setSelectedRecordId(e.target.value)} className={inputCls} disabled={previewLoading}>
                        <option value="">— 选择一次高分演练 —</option>
                        {filteredCandidates.map((r) => (
                          <option key={r.id} value={r.id}>[{r.grade}·{r.score}] {r.user_name || r.username || '匿名'} · {sceneName(r.scene_id)} · {r.fault_name} · {fmtTime(r.created_at)}</option>
                        ))}
                      </select>
                      <div className="flex items-center gap-2">
                        <button type="button" disabled={!selectedRecordId || previewLoading} onClick={() => void handleLoadPreview(selectedRecordId)} className="rounded-md bg-cyan-500/20 px-3 py-1.5 text-xs text-cyan-200 hover:bg-cyan-500/30 disabled:opacity-50">
                          {previewLoading ? '生成中…' : '生成时间轴草稿'}
                        </button>
                        {preview && <button type="button" onClick={handleClearPreview} className="rounded-md border border-border px-3 py-1.5 text-xs text-text-secondary hover:bg-bg-tertiary">清空草稿</button>}
                      </div>
                      {draftTimeline && (
                        <div className="rounded-md border border-border bg-bg-tertiary/40 p-2">
                          <div className="mb-1.5 flex items-center justify-between text-xs text-text-secondary">
                            <span className="flex items-center gap-1"><PlayCircle className="h-3.5 w-3.5" /> 时间轴 {draftTimeline.events.length} 个事件 · 总长 {draftTimeline.duration_sec}s</span>
                            <button type="button" onClick={() => setPreviewPlayerOpen((v) => !v)} className="flex items-center gap-1 rounded border border-emerald-300 bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700 hover:bg-emerald-100">
                              <PlayCircle className="h-3 w-3" />{previewPlayerOpen ? '收起' : '预览'}
                            </button>
                          </div>
                          <div className="max-h-48 space-y-1 overflow-y-auto pr-1">
                            {draftTimeline.events.map((ev, i) => (
                              <div key={i} className="flex items-center gap-2 rounded border border-border bg-bg-primary px-2 py-1 text-xs">
                                <span className="font-mono text-text-muted w-12 shrink-0">{Math.floor(ev.t / 60).toString().padStart(2, '0')}:{(ev.t % 60).toString().padStart(2, '0')}</span>
                                <span className="min-w-0 flex-1 truncate">{ev.type === 'action' ? `${ev.targetLabel || ev.target} ${ev.from}→${ev.to}` : ev.text || ev.targetLabel}</span>
                                <button type="button" onClick={() => handleRemoveDraftEvent(i)} className="text-text-muted hover:text-red-400"><X className="h-3.5 w-3.5" /></button>
                              </div>
                            ))}
                          </div>
                          {previewPlayerOpen && (
                            <div className="mt-3 border-t border-border pt-3">
                              <TimelineCompare expert={draftTimeline} student={null} expertName={form.expert_name || '老师傅'} expertTitle={form.title || ''} studentLabel="学生（待录入）" onClose={() => setPreviewPlayerOpen(false)} />
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* 表单字段 - 两列布局 */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>场景 <span className="text-red-400">*</span></label>
                  <select value={form.scene_id} onChange={(e) => setForm((f) => ({ ...f, scene_id: e.target.value }))} className={inputCls} disabled={distilling}>
                    {sceneOptions.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>采集方式</label>
                  <select value={form.source_type} onChange={(e) => setForm((f) => ({ ...f, source_type: e.target.value }))} className={inputCls} disabled={distilling}>
                    {SOURCE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className={labelCls}>故障名称 <span className="text-red-400">*</span></label>
                <input value={form.fault_name} onChange={(e) => setForm((f) => ({ ...f, fault_name: e.target.value }))} placeholder="如：再生器温度异常升高" className={inputCls} disabled={distilling} />
              </div>
              <div>
                <label className={labelCls}>经验标题 <span className="text-red-400">*</span></label>
                <input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="如：再生器超温的先兆判断与处置节奏" className={inputCls} disabled={distilling} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}><span className="inline-flex items-center gap-1"><User className="w-3 h-3" />专家姓名</span></label>
                  <input value={form.expert_name} onChange={(e) => setForm((f) => ({ ...f, expert_name: e.target.value }))} placeholder="选填" className={inputCls} disabled={distilling} />
                </div>
                <div>
                  <label className={labelCls}>职称/工龄</label>
                  <input value={form.expert_title} onChange={(e) => setForm((f) => ({ ...f, expert_title: e.target.value }))} placeholder="选填" className={inputCls} disabled={distilling} />
                </div>
              </div>
              <div>
                <label className={labelCls}>专家口述记录 <span className="text-red-400">*</span></label>
                <textarea value={form.raw_transcript} onChange={(e) => setForm((f) => ({ ...f, raw_transcript: e.target.value }))} placeholder="粘贴专家口述录音转文字内容。例如：'再生器温度往上跑，第一反应不是去降温，是先看进料量...'" className={`${inputCls} min-h-[160px] resize-y leading-relaxed`} disabled={distilling} />
                <div className="flex items-center justify-between mt-1">
                  <span className="text-[10px] text-text-muted">{form.raw_transcript.length} 字</span>
                  <span className="text-[10px] text-text-muted">{isGuest ? 'AI 蒸馏约需 15-30 秒（推理模型）' : 'AI 蒸馏约需 10-15 秒'}</span>
                </div>
              </div>

              {distilling ? (
                <div className="rounded-lg border border-cyan-500/40 bg-cyan-500/5 p-4 flex items-center gap-3">
                  <Loader2 className="w-6 h-6 text-cyan-400 animate-spin flex-shrink-0" />
                  <div className="min-w-0">
                    <div className="text-sm text-cyan-400 font-medium">AI正在蒸馏专家经验...</div>
                    <div className="text-xs text-text-secondary mt-0.5 transition-all">{DISTILL_TIPS[tipIdx]}</div>
                  </div>
                </div>
              ) : (
                <button onClick={() => void handleCollect()} disabled={!canSubmit} className="w-full px-4 py-3 text-sm bg-primary hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-lg font-medium flex items-center justify-center gap-2 transition-colors shadow-lg shadow-primary/20">
                  <Sparkles className="w-4 h-4" /> 开始蒸馏
                </button>
              )}
            </div>
          </div>

          {/* 蒸馏结果（蒸馏完成后内联展示） */}
          {latestDistilled && !distilling && (
            <div className="mt-5 rounded-xl border border-cyan-500/40 bg-bg-secondary overflow-hidden shadow-lg animate-in fade-in slide-in-from-bottom-2 duration-300">
              <div className="px-6 py-4 border-b border-border bg-gradient-to-r from-cyan-500/15 to-transparent flex items-center justify-between">
                <h3 className="text-base font-bold flex items-center gap-2">
                  <Lightbulb className="w-4 h-4 text-cyan-400" /> 蒸馏结果
                </h3>
                <button onClick={() => setLatestDistilled(null)} className="text-text-muted hover:text-text-primary p-1 rounded hover:bg-bg-tertiary">
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="p-6">
                <DistilledView data={latestDistilled} />
              </div>
            </div>
          )}

          {/* 提示信息 */}
          {!latestDistilled && !distilling && (
            <div className="mt-5 text-center text-xs text-text-muted py-4">
              <Sparkles className="w-5 h-5 mx-auto mb-2 opacity-40" />
              填写上方表单并点击"开始蒸馏"，AI将自动提炼结构化教学知识
              <br />
              <span className="opacity-60">蒸馏结果将在此处展示，并自动存入经验库</span>
            </div>
          )}
        </div>
      </div>

      {/* —— 右侧经验库抽屉 —— */}
      {drawerOpen && (
        <>
          {/* 遮罩 */}
          <div
            className="fixed inset-0 bg-black/50 z-40 transition-opacity"
            onClick={() => setDrawerOpen(false)}
          />
          {/* 抽屉 */}
          <div className="fixed right-0 top-0 bottom-0 w-full max-w-md bg-bg-secondary border-l border-border z-50 flex flex-col shadow-2xl animate-in slide-in-from-right duration-300">
            {/* 抽屉头部 */}
            <div className="flex-shrink-0 px-5 py-4 border-b border-border bg-bg-tertiary/30 flex items-center justify-between">
              <div>
                <h3 className="text-sm font-bold flex items-center gap-2">
                  <Database className="w-4 h-4 text-primary" /> 经验库
                  <span className="text-xs font-normal text-text-muted">({experiences.length})</span>
                </h3>
                <p className="text-[10px] text-text-muted mt-0.5">{sceneCount}场景 · {expertCount}位专家</p>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => void loadList()} className="p-1.5 text-text-muted hover:text-text-primary hover:bg-bg-tertiary rounded" title="刷新">
                  <RefreshCw className="w-4 h-4" />
                </button>
                <button onClick={() => setDrawerOpen(false)} className="p-1.5 text-text-muted hover:text-text-primary hover:bg-bg-tertiary rounded" title="关闭">
                  <PanelRightClose className="w-4 h-4" />
                </button>
              </div>
            </div>
            {/* 抽屉内容 */}
            <div className="flex-1 overflow-y-auto p-4">
              {listLoading ? (
                <div className="py-10 text-center text-sm text-text-muted flex items-center justify-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" /> 加载中...
                </div>
              ) : experiences.length === 0 ? (
                <div className="py-12 text-center">
                  <BookOpen className="w-8 h-8 text-text-muted mx-auto mb-2 opacity-50" />
                  <p className="text-sm text-text-muted">暂无经验记录</p>
                </div>
              ) : (
                <div className="space-y-2.5">
                  {experiences.map((exp) => (
                    <ExperienceCard
                      key={exp.id}
                      exp={exp}
                      expanded={expandedId === exp.id}
                      redistilling={redistillingId === exp.id}
                      isGuest={isGuest}
                      onToggle={() => setExpandedId(expandedId === exp.id ? null : exp.id)}
                      onArchive={() => void handleArchive(exp)}
                      onRedistill={() => void handleRedistill(exp.id)}
                      onDelete={() => void handleDelete(exp)}
                      onRename={(title) => handleRename(exp, title)}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// =============================================================
// 蒸馏结果渲染
// =============================================================
const DistilledView: React.FC<{ data: DistilledExperience }> = ({ data }) => {
  return (
    <div className="space-y-4">
      {data.master_insight && (
        <div className="p-4 rounded-lg border border-cyan-500/40 bg-cyan-500/5">
          <div className="flex items-center gap-1.5 text-xs font-medium text-cyan-400 mb-1.5"><Target className="w-3.5 h-3.5" /> 核心经验</div>
          <p className="text-sm text-text-primary leading-relaxed">{data.master_insight}</p>
        </div>
      )}
      {data.key_decisions && data.key_decisions.length > 0 && (
        <div>
          <div className="text-xs font-medium text-text-secondary mb-2 flex items-center gap-1.5"><Target className="w-3.5 h-3.5" /> 关键操作决策</div>
          <ol className="space-y-2">
            {data.key_decisions.map((d, i) => (
              <li key={i} className="p-3 rounded-lg bg-bg-tertiary/50 border border-border">
                <div className="flex items-start gap-2">
                  <span className="flex-shrink-0 w-5 h-5 rounded-full bg-primary/20 text-primary text-[10px] font-bold flex items-center justify-center">{d.step ?? i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium text-text-primary">{d.action}</span>
                      {d.priority && <span className={`text-[10px] px-1.5 py-0.5 rounded border ${PRIORITY_STYLE[d.priority] ?? ''}`}>{PRIORITY_LABEL[d.priority] ?? d.priority}</span>}
                      {d.timing && <span className="text-[10px] text-text-muted flex items-center gap-0.5"><Clock className="w-3 h-3" />{d.timing}</span>}
                    </div>
                    {d.reasoning && <p className="text-xs text-text-secondary mt-1 leading-relaxed">{d.reasoning}</p>}
                  </div>
                </div>
              </li>
            ))}
          </ol>
        </div>
      )}
      {data.param_thresholds && data.param_thresholds.length > 0 && (
        <div>
          <div className="text-xs font-medium text-text-secondary mb-2 flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5" /> 参数敏感区间</div>
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-xs">
              <thead className="bg-bg-tertiary"><tr><th className="px-3 py-1.5 text-left text-text-muted font-medium">参数</th><th className="px-3 py-1.5 text-left text-text-muted font-medium">危险区间</th><th className="px-3 py-1.5 text-left text-text-muted font-medium">专家处置</th><th className="px-3 py-1.5 text-left text-text-muted font-medium">正常范围</th></tr></thead>
              <tbody className="divide-y divide-border">
                {data.param_thresholds.map((p, i) => (
                  <tr key={i} className="bg-bg-secondary"><td className="px-3 py-1.5 text-text-primary font-medium whitespace-nowrap">{p.param}</td><td className="px-3 py-1.5 text-red-400">{p.danger_zone}</td><td className="px-3 py-1.5 text-text-secondary">{p.expert_action}</td><td className="px-3 py-1.5 text-green-400 whitespace-nowrap">{p.normal_range}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {data.rhythm && (
        <div className="flex items-start gap-2 p-3 rounded-lg bg-bg-tertiary/50 border border-border">
          <Clock className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
          <div><div className="text-xs font-medium text-blue-400 mb-0.5">操作节奏</div><p className="text-sm text-text-secondary leading-relaxed">{data.rhythm}</p></div>
        </div>
      )}
      {data.intuition_rules && data.intuition_rules.length > 0 && (
        <div>
          <div className="text-xs font-medium text-text-secondary mb-2 flex items-center gap-1.5"><Zap className="w-3.5 h-3.5" /> 经验直觉</div>
          <ul className="space-y-1">{data.intuition_rules.map((r, i) => <li key={i} className="text-xs text-text-secondary flex items-start gap-1.5 p-2 rounded bg-bg-tertiary/30"><Zap className="w-3 h-3 text-yellow-400 flex-shrink-0 mt-0.5" /><span className="leading-relaxed">{r}</span></li>)}</ul>
        </div>
      )}
      {data.common_mistakes && data.common_mistakes.length > 0 && (
        <div>
          <div className="text-xs font-medium text-text-secondary mb-2 flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5" /> 新手常见错误</div>
          <div className="space-y-2">
            {data.common_mistakes.map((m, i) => (
              <div key={i} className="p-3 rounded-lg bg-red-500/5 border border-red-500/20">
                <div className="text-sm text-red-400 font-medium">{m.mistake}</div>
                <div className="text-xs text-text-muted mt-1"><span className="text-text-secondary">原因：</span>{m.why_wrong}</div>
                <div className="text-xs text-green-400 mt-0.5"><span className="text-text-secondary">正确做法：</span>{m.correct_alternative}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// =============================================================
// 经验库卡片
// =============================================================
const ExperienceCard: React.FC<{
  exp: ExperienceRule; expanded: boolean; redistilling: boolean; isGuest: boolean;
  onToggle: () => void; onArchive: () => void; onRedistill: () => void; onDelete: () => void; onRename: (title: string) => Promise<void>;
}> = ({ exp, expanded, redistilling, isGuest, onToggle, onArchive, onRedistill, onDelete, onRename }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(exp.title);
  const [showTimeline, setShowTimeline] = useState(false);
  const [saving, setSaving] = useState(false);
  const ss = getSceneStyle(exp.scene_id);

  useEffect(() => { setDraft(exp.title); }, [exp.title]);

  const startEdit = (e: React.MouseEvent) => { e.stopPropagation(); setEditing(true); setDraft(exp.title); };
  const cancelEdit = (e: React.MouseEvent) => { e.stopPropagation(); setEditing(false); setDraft(exp.title); };
  const saveEdit = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!draft.trim() || draft.trim() === exp.title) { setEditing(false); return; }
    setSaving(true);
    try { await onRename(draft.trim()); setEditing(false); } catch { /* */ } finally { setSaving(false); }
  };

  return (
    <div className={`rounded-lg border border-border border-l-2 ${ss.bar} bg-bg-secondary overflow-hidden transition-all hover:shadow-md ${expanded ? 'shadow-md' : ''}`}>
      {/* 卡片头部 */}
      <div className="flex items-center gap-3 p-3 cursor-pointer hover:bg-bg-tertiary/30" onClick={onToggle}>
        <div className="flex-1 min-w-0">
          {editing ? (
            <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
              <input value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') void saveEdit(e as any); if (e.key === 'Escape') void cancelEdit(e as any); }} className="flex-1 px-2 py-1 text-sm bg-bg-tertiary border border-primary/50 rounded text-text-primary focus:outline-none" autoFocus />
              <button onClick={saveEdit} disabled={saving} title="保存" className="p-1 text-green-400 hover:bg-green-400/10 rounded disabled:opacity-50">{saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}</button>
              <button onClick={cancelEdit} title="取消" className="p-1 text-text-muted hover:text-text-primary rounded"><X className="w-3.5 h-3.5" /></button>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`text-[10px] px-1.5 py-0.5 rounded ${ss.badge} flex items-center gap-1`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${ss.dot}`} />
                  {sceneName(exp.scene_id)}
                </span>
                <span className="text-sm font-medium text-text-primary truncate">{exp.title}</span>
              </div>
              <div className="flex items-center gap-2 text-[11px] text-text-muted mt-1 flex-wrap">
                <span className="truncate max-w-[120px]">{exp.fault_name}</span>
                {exp.expert_name && <span className="flex items-center gap-0.5"><User className="w-3 h-3" />{exp.expert_name}{exp.expert_title ? ` · ${exp.expert_title}` : ''}</span>}
                <span>{fmtTime(exp.created_at)}</span>
                {exp.distilled && <span className="text-cyan-400 flex items-center gap-0.5"><Lightbulb className="w-3 h-3" />已蒸馏</span>}
              </div>
            </>
          )}
        </div>
        <div className="flex items-center gap-0.5 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
          {!isGuest && <button onClick={startEdit} title="编辑标题" className="p-1.5 text-text-muted hover:text-text-primary hover:bg-bg-tertiary rounded"><Edit3 className="w-3.5 h-3.5" /></button>}
          <button onClick={onRedistill} disabled={redistilling} title="重新蒸馏" className="p-1.5 text-cyan-400 hover:bg-cyan-400/10 rounded disabled:opacity-50">{redistilling ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}</button>
          {!isGuest && <button onClick={onArchive} title="归档" className="px-2 py-1 text-[10px] text-text-muted hover:text-yellow-400 hover:bg-yellow-400/10 rounded border border-border">归档</button>}
          {!isGuest && <button onClick={onDelete} title="删除" className="p-1.5 text-text-muted hover:text-red-400 hover:bg-red-400/10 rounded"><Trash2 className="w-3.5 h-3.5" /></button>}
          <button onClick={onToggle} className="p-1 text-text-muted hover:text-text-primary">{expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}</button>
        </div>
      </div>
      {/* 展开内容 */}
      {expanded && (
        <div className="border-t border-border p-4 bg-bg-tertiary/20 space-y-3">
          {exp.distill_error ? (
            <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
              <div><div className="text-sm text-red-400 font-medium">蒸馏失败</div><p className="text-xs text-text-secondary mt-1 break-words">{exp.distill_error}</p>
              <button onClick={onRedistill} disabled={redistilling} className="mt-2 px-3 py-1.5 text-xs bg-cyan-500/15 text-cyan-400 border border-cyan-500/40 rounded hover:bg-cyan-500/25 flex items-center gap-1.5 disabled:opacity-50">{redistilling ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} 重新蒸馏</button></div>
            </div>
          ) : exp.distilled ? <DistilledView data={exp.distilled} /> : <div className="text-xs text-text-muted text-center py-4">暂无蒸馏结果</div>}
          {exp.timeline && exp.timeline.events && exp.timeline.events.length > 0 && (
            <div className="mt-3">
              <button type="button" onClick={() => setShowTimeline((v) => !v)} className="flex items-center gap-1 rounded border border-emerald-300 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-100">
                <PlayCircle className="h-3.5 w-3.5" />{showTimeline ? '收起时间轴' : `预览时间轴（${exp.timeline.events.length} 事件 / ${exp.timeline.duration_sec}s）`}
              </button>
              {showTimeline && <div className="mt-2"><TimelineCompare expert={exp.timeline} student={null} expertName={exp.expert_name || '老师傅'} expertTitle={exp.title} studentLabel="学生（参考用）" onClose={() => setShowTimeline(false)} /></div>}
            </div>
          )}
          {exp.raw_transcript && (
            <details className="text-xs">
              <summary className="text-text-muted cursor-pointer hover:text-text-secondary select-none">查看原始口述记录</summary>
              <p className="text-text-secondary mt-2 p-2 bg-bg-secondary rounded border border-border whitespace-pre-wrap leading-relaxed">{exp.raw_transcript}</p>
            </details>
          )}
        </div>
      )}
    </div>
  );
};
